import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, Sparkles, Trophy, PartyPopper, Heart, Medal, Award } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Motivational lines — gold/silver/bronze/decent get their own pools so
// the tone matches the medal. Below-50 customers don't see the login
// celebration at all (they get a smaller one when placing orders).
const QUOTES_GOLD = [
  "👑 الملك وصل! أنت زبون الزبائن — لا أحد يجاريك",
  "🥇 صاحب الذوق الراقي بنفسه! الذهب يستحقّك",
  "🌟 أنت الأول والأوحد — نوّرت الشاشة!",
];
const QUOTES_SILVER = [
  "🥈 المركز الثاني — على بُعد طلب من الذهب! اضرب الآن",
  "✨ تشتاقلك الفلوس قبل ما تشتاقها — استمر!",
  "💪 خطوة وحدة منفصلك عن العرش — يلا!",
];
const QUOTES_BRONZE = [
  "🥉 الثالث — التوب 3 بنفسه! استمر تقدر تكون الأول",
  "🏆 من النخبة — كل طلب يقرّبك من الذهب",
  "🔥 ما يطفي حماسك شي — مكانك بين النجوم",
];
const QUOTES_NICE = [
  "🌟 ضمن أفضل 50 زبون — أهلاً ببطلنا!",
  "💎 من زبائن الصفوة — استمر تجي للأمام",
  "🎯 طلباتك تحكي قصة نجاح — يلا للنجوم",
  "💰 كل كرتون يقرّبك للمنصة الذهبية",
];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const RANK_TIERS = {
  gold: {
    ring: "from-[#FFD700] via-[#FFC107] to-[#FF8C00]",
    ringBorder: "border-yellow-600",
    cardBg: "from-yellow-50 via-amber-100 to-orange-100",
    cardBorder: "border-amber-400",
    textHead: "text-amber-900",
    badge: "from-yellow-400 to-amber-500",
    accent: "#F59E0B",
    icon: Crown,
    label: "ذهبي",
    confetti: 140,
    balloons: 14,
    sparkles: 12,
    duration: 5500,
  },
  silver: {
    ring: "from-[#E5E7EB] via-[#9CA3AF] to-[#6B7280]",
    ringBorder: "border-slate-500",
    cardBg: "from-slate-50 via-slate-100 to-gray-200",
    cardBorder: "border-slate-400",
    textHead: "text-slate-800",
    badge: "from-slate-300 to-slate-500",
    accent: "#94A3B8",
    icon: Medal,
    label: "فضي",
    confetti: 110,
    balloons: 12,
    sparkles: 10,
    duration: 5000,
  },
  bronze: {
    ring: "from-[#FCD34D] via-[#D97706] to-[#92400E]",
    ringBorder: "border-amber-800",
    cardBg: "from-orange-50 via-amber-100 to-orange-200",
    cardBorder: "border-orange-500",
    textHead: "text-orange-900",
    badge: "from-orange-400 to-amber-700",
    accent: "#B45309",
    icon: Award,
    label: "برونزي",
    confetti: 90,
    balloons: 10,
    sparkles: 8,
    duration: 4500,
  },
  nice: {
    ring: "from-blue-200 via-blue-400 to-blue-600",
    ringBorder: "border-blue-700",
    cardBg: "from-white via-blue-50 to-blue-100",
    cardBorder: "border-blue-400",
    textHead: "text-blue-900",
    badge: "from-blue-400 to-blue-600",
    accent: "#3B82F6",
    icon: Trophy,
    label: "نخبة",
    confetti: 60,
    balloons: 6,
    sparkles: 5,
    duration: 3500,
  },
};

const BALLOONS = ["🎈", "🎉", "🎊", "🎀", "✨", "🌟", "💫"];
const COLORS_GOLD = ["#FFD700", "#FFA500", "#FBBF24", "#F59E0B", "#FCD34D", "#FFFFFF"];
const COLORS_SILVER = ["#E5E7EB", "#9CA3AF", "#6B7280", "#D1D5DB", "#FFFFFF", "#94A3B8"];
const COLORS_BRONZE = ["#D97706", "#B45309", "#92400E", "#F59E0B", "#FCD34D", "#FFFFFF"];
const COLORS_NICE = ["#3B82F6", "#10B981", "#F5A623", "#EC4899", "#A855F7", "#06B6D4"];

const colorsFor = (tier) => ({ gold: COLORS_GOLD, silver: COLORS_SILVER, bronze: COLORS_BRONZE, nice: COLORS_NICE }[tier]);

const Confetti = ({ count = 60, colors }) => {
  const [pieces] = useState(() =>
    Array.from({ length: count }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.8,
      duration: 2.5 + Math.random() * 2,
      size: 6 + Math.random() * 10,
      color: colors[i % colors.length],
      rotate: Math.random() * 360,
      shape: Math.random() > 0.5 ? "50%" : "2px",
    })),
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          initial={{ y: -40, opacity: 1, rotate: p.rotate }}
          animate={{ y: "110vh", opacity: 0, rotate: p.rotate + 720 }}
          transition={{ delay: p.delay, duration: p.duration, ease: "linear" }}
          style={{
            position: "absolute",
            top: 0,
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 1.4,
            background: p.color,
            borderRadius: p.shape,
            boxShadow: `0 0 6px ${p.color}90`,
          }}
        />
      ))}
    </div>
  );
};

const Sparkle = ({ delay, top, left, size = 20, color = "#FBBF24" }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0, rotate: 0 }}
    animate={{ opacity: [0, 1, 1, 0], scale: [0, 1.3, 1, 0], rotate: [0, 90, 180, 270] }}
    transition={{ delay, duration: 1.8, repeat: 2 }}
    style={{ position: "absolute", top, left, color, fontSize: size }}
  >
    <Sparkles className="w-full h-full" />
  </motion.div>
);

const Balloon = ({ emoji, delay, x }) => (
  <motion.div
    initial={{ y: "110vh", opacity: 0, rotate: -10 }}
    animate={{ y: "-20vh", opacity: [0, 1, 1, 0.6, 0], rotate: [-10, 10, -10, 5, -5] }}
    transition={{ delay, duration: 4, ease: "easeOut" }}
    style={{ position: "absolute", left: `${x}%`, fontSize: 38 }}
    className="pointer-events-none"
  >
    {emoji}
  </motion.div>
);

// Decide rank tier:
// • rank 1 → gold • 2 → silver • 3 → bronze • 4–50 → nice • >50 → null (suppress)
// Customers with ZERO committed orders ALWAYS get null — the login
// celebration is a reward for activity, not just for signing in.
const tierForRank = (rank, orders) => {
  if (!orders || orders <= 0) return null;
  if (!rank || rank <= 0) return null;
  if (rank === 1) return "gold";
  if (rank === 2) return "silver";
  if (rank === 3) return "bronze";
  if (rank <= 50) return "nice";
  return null;
};

export default function LoginCelebration({ user, onClose }) {
  const [info, setInfo] = useState(null);
  const [show, setShow] = useState(true);
  const [ready, setReady] = useState(false);
  const [tier, setTier] = useState(null);
  const [quote, setQuote] = useState("");

  // Fetch rank, then decide tier + quote ONCE. If the customer ranks below
  // 50 (or has zero orders), we suppress the login celebration entirely.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/user/rank`);
        if (!alive) return;
        const t = tierForRank(data?.rank, data?.orders);
        setInfo(data);
        if (!t) {
          // No celebration for this user this login → dismiss immediately.
          setShow(false);
          return;
        }
        setTier(t);
        const pool = t === "gold" ? QUOTES_GOLD : t === "silver" ? QUOTES_SILVER : t === "bronze" ? QUOTES_BRONZE : QUOTES_NICE;
        setQuote(pick(pool));
        setReady(true);
      } catch {
        if (alive) setShow(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Auto-dismiss timer — duration scales with tier (longer for gold).
  useEffect(() => {
    if (!ready || !tier) return;
    const t = setTimeout(() => setShow(false), RANK_TIERS[tier].duration);
    return () => clearTimeout(t);
  }, [ready, tier]);

  const handleExit = () => setShow(false);

  // Hide outright if no tier (rank > 50 etc.)
  if (!tier && !show) {
    // Run onClose once to clear celebrateUser state in AuthContext.
    setTimeout(onClose, 0);
    return null;
  }

  if (!ready) {
    return (
      <AnimatePresence onExitComplete={onClose}>
        {show && <motion.div className="fixed inset-0 z-[9999] pointer-events-none" />}
      </AnimatePresence>
    );
  }

  const meta = RANK_TIERS[tier];
  const Icon = meta.icon;
  const colors = colorsFor(tier);
  const name = user?.customer_name || user?.full_name || user?.market_name || "صديقي";
  const rankLabel = info?.rank ? `#${info.rank}` : "—";

  return (
    <AnimatePresence onExitComplete={onClose}>
      {show && (
        <motion.div
          key="celebration"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={handleExit}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{
            background:
              tier === "gold"
                ? "radial-gradient(ellipse at center, rgba(120,53,15,0.7) 0%, rgba(0,0,0,0.9) 100%)"
                : "radial-gradient(ellipse at center, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.88) 100%)",
          }}
          data-testid="login-celebration"
          data-rank-tier={tier}
        >
          <Confetti count={meta.confetti} colors={colors} />
          {Array.from({ length: meta.balloons }).map((_, i) => (
            <Balloon key={i} emoji={BALLOONS[i % BALLOONS.length]} delay={0.15 + i * 0.14} x={4 + i * (95 / meta.balloons)} />
          ))}
          {Array.from({ length: meta.sparkles }).map((_, i) => {
            const positions = [
              { top: "12%", left: "10%", size: 30 },
              { top: "18%", left: "82%", size: 26 },
              { top: "35%", left: "5%", size: 22 },
              { top: "40%", left: "90%", size: 28 },
              { top: "60%", left: "8%", size: 24 },
              { top: "65%", left: "85%", size: 30 },
              { top: "78%", left: "20%", size: 22 },
              { top: "82%", left: "70%", size: 26 },
              { top: "25%", left: "50%", size: 18 },
              { top: "55%", left: "50%", size: 20 },
              { top: "8%", left: "45%", size: 22 },
              { top: "88%", left: "45%", size: 24 },
            ];
            const p = positions[i % positions.length];
            return <Sparkle key={i} delay={0.3 + i * 0.12} top={p.top} left={p.left} size={p.size} color={meta.accent} />;
          })}

          <motion.div
            initial={{ scale: 0.4, rotate: -8, y: 80 }}
            animate={{ scale: 1, rotate: 0, y: 0 }}
            exit={{ scale: 0.6, opacity: 0, y: -40 }}
            transition={{ type: "spring", damping: 14, stiffness: 220 }}
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
            className={`relative max-w-sm w-full bg-gradient-to-br ${meta.cardBg} rounded-3xl shadow-2xl p-6 text-center overflow-hidden border-4 ${meta.cardBorder}`}
          >
            <div className="absolute -top-12 -left-12 w-32 h-32 rounded-full blur-2xl pointer-events-none" style={{ background: `${meta.accent}40` }} />
            <div className="absolute -bottom-12 -right-12 w-32 h-32 rounded-full blur-2xl pointer-events-none" style={{ background: `${meta.accent}30` }} />

            <motion.div
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.15 }}
              className={`relative inline-flex items-center gap-2 bg-gradient-to-l ${meta.badge} text-white text-xs font-extrabold px-3 py-1 rounded-full shadow-lg mb-3`}
            >
              <PartyPopper className="w-3.5 h-3.5" />
              {tier === "gold" ? "ملك الجملة" : tier === "silver" ? "النائب" : tier === "bronze" ? "التوب 3" : "النخبة"}
            </motion.div>

            <motion.h2
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.25, type: "spring", damping: 10 }}
              className={`font-heading text-2xl sm:text-3xl font-extrabold mb-1 ${meta.textHead}`}
            >
              {name}
            </motion.h2>

            <motion.p
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className={`text-sm leading-relaxed mb-4 font-bold ${meta.textHead}`}
            >
              {quote}
            </motion.p>

            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.55, type: "spring", damping: 8, stiffness: 200 }}
              className={`relative mx-auto w-44 h-44 rounded-full flex flex-col items-center justify-center shadow-2xl border-4 bg-gradient-to-br ${meta.ring} ${meta.ringBorder}`}
            >
              <Icon className={`w-7 h-7 mb-1 ${tier === "gold" || tier === "bronze" ? "text-white" : "text-white"} drop-shadow-lg`} />
              <div className="text-[10px] font-bold text-white/95 uppercase tracking-wider">ترتيبك</div>
              <div className="font-heading text-5xl font-black tabular-nums leading-none text-white drop-shadow-md" dir="ltr" data-testid="rank-value">
                {rankLabel}
              </div>
              <div className="text-[10px] font-bold mt-1 text-white/90">
                من بين <b dir="ltr">{info?.total_customers || "—"}</b> زبون
              </div>
              <span className={`absolute -bottom-2 right-1/2 translate-x-1/2 bg-white text-[10px] font-extrabold px-2.5 py-0.5 rounded-full shadow-md ${meta.textHead}`}>
                {meta.label}
              </span>
            </motion.div>

            <motion.div
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-5 flex items-center justify-center gap-2 text-[11px] flex-wrap"
            >
              <span className={`bg-white/80 rounded-full px-2 py-0.5 font-bold flex items-center gap-1 ${meta.textHead}`}>
                <Heart className="w-3 h-3 text-rose-500" />
                {info?.orders ?? "—"} طلب
              </span>
              <span className={`bg-white/80 rounded-full px-2 py-0.5 font-bold ${meta.textHead}`}>{info?.cartons ?? "—"} كرتون</span>
            </motion.div>

            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              onClick={handleExit}
              data-testid="celebration-dismiss"
              className={`mt-4 text-[11px] hover:underline ${meta.textHead} opacity-70`}
            >
              اضغط للمتابعة ←
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
